import React, { useState } from 'react';
import { fpBackButton, fpCard, fpHeader, fpHeaderTitle, fpSticker } from '../utils/fpUi';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Users, Mail, FileText, CheckCircle, XCircle, User, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';

export default function Commissions() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();
  const [commissionType, setCommissionType] = useState(null); // 'comic' or 'illustration'
  const [characters, setCharacters] = useState(1);
  const [details, setDetails] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [patreonUsername, setPatreonUsername] = useState('');
  const [sending, setSending] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const FORMSPREE_URL = 'https://formspree.io/f/xgooezyp';
  const MAX_CHARACTERS = 99;

  const handleCharactersChange = (value) => {
    const num = parseInt(value) || 0;
    if (num >= 0 && num <= MAX_CHARACTERS) {
      setCharacters(num);
    }
  };

  const incrementCharacters = () => {
    if (characters < MAX_CHARACTERS) {
      setCharacters(prev => prev + 1);
    }
  };

  const decrementCharacters = () => {
    if (characters > 1) {
      setCharacters(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!commissionType) {
      alert(t('pleaseSelectType'));
      return;
    }
    
    if (!details.trim()) {
      alert(t('pleaseProvideDetails'));
      return;
    }
    
    if (!contactEmail.trim()) {
      alert(t('pleaseProvideEmail'));
      return;
    }

    setSending(true);
    setSubmitStatus(null);

    // Build form data for Formspree
    const typeLabel = commissionType === 'comic' ? 'Comic' : 'Illustration';
    const subject = `Commission Request - ${typeLabel}${commissionType === 'illustration' ? ` (${characters} Character${characters !== 1 ? 's' : ''})` : ''}`;
    
    const formData = {
      _subject: subject,
      email: contactEmail,
      commission_type: typeLabel,
      characters: commissionType === 'illustration' ? characters : 'N/A',
      patreon_username: commissionType === 'illustration' && patreonUsername ? patreonUsername : 'N/A',
      details: details,
      _replyto: contactEmail,
    };

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setCommissionType(null);
        setCharacters(1);
        setDetails('');
        setContactEmail('');
        setPatreonUsername('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending form:', error);
      setSubmitStatus('error');
    }
    
    setSending(false);
  };

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? styles.containerDark : {}),
    }}>
      <div style={{
        ...styles.header,
        ...(isDarkMode ? styles.headerDark : {}),
      }}>
        <button onClick={() => navigate('/')} style={{
          ...styles.backButton,
          ...(isDarkMode ? styles.backButtonDark : {}),
        }}>
          <ArrowLeft size={20} color={isDarkMode ? "#000" : "#fff"} />
        </button>
        <h2 style={styles.title}>{t('commissions')}</h2>
        <div style={styles.spacer} />
      </div>

      <div style={styles.content}>
        {/* Info Card */}
        <div style={{
          ...styles.infoCard,
          ...(isDarkMode ? styles.cardDark : {}),
        }}>
          <div style={styles.infoIcon}>🎨</div>
          <h3 style={{
            ...styles.infoTitle,
            ...(isDarkMode ? styles.infoTitleDark : {}),
          }}>{t('customArtwork')}</h3>
          <p style={{
            ...styles.infoText,
            ...(isDarkMode ? styles.textMutedDark : {}),
          }}>
            {t('commissionsAccepted')}
          </p>
          <p style={{
            ...styles.consultText,
            ...(isDarkMode ? styles.consultTextDark : {}),
          }}>
            {t('askWithoutCommitment')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          ...styles.form,
          ...(isDarkMode ? styles.cardDark : {}),
        }}>
          {/* Commission Type Selection */}
          <div style={styles.inputGroup}>
            <label style={{
              ...styles.label,
              ...(isDarkMode ? styles.labelDark : {}),
            }}>
              <span>{t('selectCommissionType')}</span>
            </label>
            <div style={styles.typeSelection}>
              <button
                type="button"
                onClick={() => setCommissionType('comic')}
                style={{
                  ...styles.typeButton,
                  ...(isDarkMode ? styles.typeButtonDark : {}),
                  ...(commissionType === 'comic' ? (isDarkMode ? styles.typeButtonActiveDark : styles.typeButtonActive) : {}),
                }}
              >
                📖 {t('comic')}
              </button>
              <button
                type="button"
                onClick={() => setCommissionType('illustration')}
                style={{
                  ...styles.typeButton,
                  ...(isDarkMode ? styles.typeButtonDark : {}),
                  ...(commissionType === 'illustration' ? (isDarkMode ? styles.typeButtonActiveDark : styles.typeButtonActive) : {}),
                }}
              >
                🖼️ {t('illustration')}
              </button>
            </div>
          </div>

          {/* Discount Banner - Only for Illustration */}
          {commissionType === 'illustration' && (
            <div style={{
              ...styles.discountBanner,
              ...(isDarkMode ? styles.discountBannerDark : {}),
            }}>
              <span style={styles.patreonIcon}>🎁</span>
              <span>{t('masterTierDiscount')}</span>
            </div>
          )}

          {/* Comic No Discount Notice */}
          {commissionType === 'comic' && (
            <div style={{
              ...styles.noDiscountBanner,
              ...(isDarkMode ? styles.noDiscountBannerDark : {}),
            }}>
              <Info size={18} />
              <span>{t('comicNoDiscount')}</span>
            </div>
          )}

          {/* Characters Input - Only for Illustration */}
          {commissionType === 'illustration' && (
            <div style={styles.inputGroup}>
              <label style={{
                ...styles.label,
                ...(isDarkMode ? styles.labelDark : {}),
              }}>
                <Users size={18} color={isDarkMode ? "#FFD700" : "#FF1493"} />
                <span>{t('numberOfCharacters')}</span>
              </label>
              <div style={styles.numberInputContainer}>
                <button
                  type="button"
                  onClick={decrementCharacters}
                  style={{
                    ...styles.numberButton,
                    ...(isDarkMode ? styles.numberButtonDark : {}),
                  }}
                  disabled={characters <= 1}
                >
                  −
                </button>
                <input
                  type="number"
                  value={characters}
                  onChange={(e) => handleCharactersChange(e.target.value)}
                  min="1"
                  max={MAX_CHARACTERS}
                  style={{
                    ...styles.numberInput,
                    ...(isDarkMode ? styles.inputDark : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={incrementCharacters}
                  style={{
                    ...styles.numberButton,
                    ...(isDarkMode ? styles.numberButtonDark : {}),
                  }}
                  disabled={characters >= MAX_CHARACTERS}
                >
                  +
                </button>
              </div>
            </div>
          )}

          {/* Details Input */}
          <div style={styles.inputGroup}>
            <label style={{
              ...styles.label,
              ...(isDarkMode ? styles.labelDark : {}),
            }}>
              <FileText size={18} color={isDarkMode ? "#FFD700" : "#FF1493"} />
              <span>{t('details')}</span>
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={commissionType === 'comic' 
                ? t('comicPlaceholder')
                : t('illustrationPlaceholder')}
              style={{
                ...styles.textarea,
                ...(isDarkMode ? styles.inputDark : {}),
              }}
              rows={6}
              required
            />
          </div>

          {/* Patreon Username - Only for Illustration */}
          {commissionType === 'illustration' && (
            <div style={styles.inputGroup}>
              <label style={{
                ...styles.label,
                ...(isDarkMode ? styles.labelDark : {}),
              }}>
                <User size={18} color={isDarkMode ? "#FFD700" : "#FF1493"} />
                <span>{t('patreonUsername')}</span>
              </label>
              <input
                type="text"
                value={patreonUsername}
                onChange={(e) => setPatreonUsername(e.target.value)}
                placeholder={t('patreonUsernamePlaceholder')}
                style={{
                  ...styles.textInput,
                  ...(isDarkMode ? styles.inputDark : {}),
                }}
              />
            </div>
          )}

          {/* Contact Email Input */}
          <div style={styles.inputGroup}>
            <label style={{
              ...styles.label,
              ...(isDarkMode ? styles.labelDark : {}),
            }}>
              <Mail size={18} color={isDarkMode ? "#FFD700" : "#FF1493"} />
              <span>{t('yourContactEmail')}</span>
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder={t('emailPlaceholder')}
              style={{
                ...styles.textInput,
                ...(isDarkMode ? styles.inputDark : {}),
              }}
              required
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              ...(sending || !commissionType ? styles.submitButtonDisabled : {}),
            }}
            disabled={sending || !commissionType}
          >
            <Send size={20} color="#000" />
            <span>{sending ? t('sending') : t('sendCommissionRequest')}</span>
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div style={styles.successMessage}>
              <CheckCircle size={20} color="#22c55e" />
              <span>{t('messageSentSuccess')}</span>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div style={styles.errorMessage}>
              <XCircle size={20} color="#ef4444" />
              <span>{t('messageSentError')}</span>
            </div>
          )}

          <p style={{
            ...styles.disclaimer,
            ...(isDarkMode ? styles.textMutedDark : {}),
          }}>
            {t('formspreeDisclaimer')}
          </p>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundImage: 'url(/wallpaper.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    display: 'flex',
    flexDirection: 'column',
  },
  containerDark: {
    backgroundImage: 'none',
    backgroundColor: '#000000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    ...fpHeader,
  },
  headerDark: {
    backgroundColor: '#000000',
    borderBottom: '1px solid #333',
    boxShadow: 'none',
  },
  backButton: {
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.4)',
    ...fpBackButton,
  },
  backButtonDark: {
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(255, 255, 255, 0.2)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    ...fpHeaderTitle,
  },
  spacer: {
    width: '44px',
  },
  content: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '600px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  infoCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(255, 20, 147, 0.2)',
    ...fpCard,
  },
  cardDark: {
    backgroundColor: '#111111',
    border: '1px solid #333',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
  },
  infoIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  infoTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#FF1493',
    margin: '0 0 8px 0',
  },
  infoTitleDark: {
    color: '#FFD700',
  },
  infoText: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 8px 0',
    lineHeight: '1.5',
  },
  textMutedDark: {
    color: '#aaaaaa',
  },
  consultText: {
    fontSize: '13px',
    color: '#FF1493',
    margin: 0,
    fontWeight: '600',
    fontStyle: 'italic',
  },
  consultTextDark: {
    color: '#FFD700',
  },
  form: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    boxShadow: '0 4px 20px rgba(255, 20, 147, 0.2)',
    ...fpCard,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  labelDark: {
    color: '#ffffff',
  },
  typeSelection: {
    display: 'flex',
    gap: '12px',
  },
  typeButton: {
    flex: 1,
    padding: '16px',
    border: '2px solid #ddd',
    borderRadius: '12px',
    backgroundColor: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#666',
  },
  typeButtonDark: {
    backgroundColor: '#1a1a1a',
    border: '2px solid #444',
    color: '#aaaaaa',
  },
  typeButtonActive: {
    borderColor: '#FF1493',
    backgroundColor: 'rgba(255, 20, 147, 0.1)',
    color: '#FF1493',
  },
  typeButtonActiveDark: {
    borderColor: '#FFD700',
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
    color: '#FFD700',
  },
  numberInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'center',
  },
  numberButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '2px solid #FF1493',
    backgroundColor: 'white',
    color: '#FF1493',
    fontSize: '24px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  numberButtonDark: {
    border: '2px solid #FFD700',
    backgroundColor: '#1a1a1a',
    color: '#FFD700',
  },
  numberInput: {
    width: '80px',
    height: '50px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    border: '2px solid #ddd',
    borderRadius: '12px',
    outline: 'none',
    color: '#333',
  },
  inputDark: {
    backgroundColor: '#1a1a1a',
    border: '2px solid #444',
    color: '#ffffff',
  },
  textarea: {
    width: '100%',
    padding: '14px',
    fontSize: '14px',
    border: '2px solid #ddd',
    borderRadius: '12px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    minHeight: '120px',
  },
  textInput: {
    width: '100%',
    padding: '14px',
    fontSize: '14px',
    border: '2px solid #ddd',
    borderRadius: '12px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px 24px',
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    color: '#000',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(255, 215, 0, 0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '8px',
    ...fpSticker,
  },
  submitButtonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  disclaimer: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#888',
    margin: 0,
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: '2px solid #22c55e',
    borderRadius: '12px',
    color: '#22c55e',
    fontSize: '14px',
    fontWeight: '600',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid #ef4444',
    borderRadius: '12px',
    color: '#ef4444',
    fontSize: '14px',
    fontWeight: '600',
  },
  discountBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '700',
    boxShadow: '0 4px 12px rgba(155, 89, 182, 0.3)',
  },
  discountBannerDark: {
    background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
  },
  patreonIcon: {
    fontSize: '20px',
  },
  noDiscountBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(255, 140, 26, 0.1)',
    border: '1px solid #ff8c1a',
    borderRadius: '12px',
    color: '#ff8c1a',
    fontSize: '14px',
    fontWeight: '500',
  },
  noDiscountBannerDark: {
    backgroundColor: 'rgba(255, 140, 26, 0.15)',
    border: '1px solid #ff8c1a',
  },
};
